import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from 'react'
import AppContext from '../../appContext'
import Dialog from '../../components/Dialog'
import QCMapPanel from '../../components/QCMapPanel'
import {SamplePoint} from '../../components/GeoMap'
import NullableMeasureValue from '../../controllers/NullableMeasureValue'
import {getValueNullable} from '../program/Helpers'
import {customUnits, unitCustom} from '../program/CustomUnits'
import {RowWithId, formatValue, formatMeasureElement} from './limeSoilUtils'

interface SampleLocationsDialogProps {
    show: boolean
    setShow: Dispatch<SetStateAction<boolean>>
    rows: RowWithId[]
    samplePoints: SamplePoint[]
    boundaryCoordinates: Array<{x: number; y: number}>
    boundaryLoading: boolean
    boundaryError: boolean
    landId?: number
    meq100gMode: boolean
}

const SampleLocationsDialog: React.FC<SampleLocationsDialogProps> = (props) => {
    const app = useContext(AppContext)
    const system = app.initial.system
    const [selectedLabel, setSelectedLabel] = useState<string | null>(null)

    useEffect(() => {
        if (!props.show) setSelectedLabel(null)
    }, [props.show])

    const unitLabel = props.meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm)

    function formatMeasure(value: NullableMeasureValue): string {
        const v = getValueNullable(system, value)
        if (v === null) return '-'
        return v.toFixed(2)
    }

    const selectedRow = selectedLabel ? props.rows.find(r => (r.referenceNr || r.landRef) === selectedLabel) : null
    const th = "px-2 py-1 text-left"
    const td = "px-2 py-1"

    return (
        <Dialog
            title={app.word('sample_locations')}
            show={props.show}
            setShow={props.setShow}
            body={
                <div className="p-4 min-w-[70vw]">
                    {props.boundaryLoading ? (
                        <div className="flex items-center justify-center h-[60vh] bg-gray-100 rounded">
                            <div className="text-gray-500">{app.word('loading')}...</div>
                        </div>
                    ) : (
                        <QCMapPanel
                            samplePoints={props.samplePoints}
                            boundaryCoordinates={props.boundaryCoordinates}
                            boundaryError={props.boundaryError}
                            landId={props.landId}
                            selectedPointLabel={selectedLabel}
                            onPointSelect={setSelectedLabel}
                        >
                            {selectedRow && (
                                <div className="absolute top-0 left-0 right-0 overflow-x-auto bg-white/90 border-b border-gray-300 shadow-sm z-10">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-200/90">
                                                <th className={th}>{app.word('reference_nr')}</th>
                                                <th className={th}>{app.word('sample_depth')}</th>
                                                <th className={th}>pH(H2O)</th>
                                                <th className={th}>P Bray I</th>
                                                <th className={th}>K ({unitLabel})</th>
                                                <th className={th}>Na ({unitLabel})</th>
                                                <th className={th}>Ca ({unitLabel})</th>
                                                <th className={th}>Mg ({unitLabel})</th>
                                                <th className={th}>Exch H</th>
                                                <th className={th}>%Ca</th>
                                                <th className={th}>%Mg</th>
                                                <th className={th}>%K</th>
                                                <th className={th}>%Na</th>
                                                <th className={th}>{app.word('acid_saturated')}</th>
                                                <th className={th}>Ca:Mg</th>
                                                <th className={th}>(Ca+Mg)/K</th>
                                                <th className={th}>Mg:K</th>
                                                <th className={th}>S-Value</th>
                                                <th className={th}>Na:K</th>
                                                <th className={th}>CEC</th>
                                                <th className={th}>{app.word('density')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className={td}>{selectedRow.referenceNr || selectedRow.landRef}</td>
                                                <td className={td}>{app.word(selectedRow.sampleDepth)}</td>
                                                <td className={td}>{formatValue(selectedRow.phH2O)}</td>
                                                <td className={td}>{formatMeasure(selectedRow.pBrayI)}</td>
                                                <td className={td}>{formatMeasureElement(system, selectedRow.k, 'K', props.meq100gMode)}</td>
                                                <td className={td}>{formatMeasureElement(system, selectedRow.na, 'Na', props.meq100gMode)}</td>
                                                <td className={td}>{formatMeasureElement(system, selectedRow.ca, 'Ca', props.meq100gMode)}</td>
                                                <td className={td}>{formatMeasureElement(system, selectedRow.mg, 'Mg', props.meq100gMode)}</td>
                                                <td className={td}>{formatValue(selectedRow.exchH)}</td>
                                                <td className={td}>{formatValue(selectedRow.caPercent)}</td>
                                                <td className={td}>{formatValue(selectedRow.mgPercent)}</td>
                                                <td className={td}>{formatValue(selectedRow.kPercent)}</td>
                                                <td className={td}>{formatValue(selectedRow.naPercent)}</td>
                                                <td className={td}>{formatValue(selectedRow.acidSat)}</td>
                                                <td className={td}>{formatValue(selectedRow.caMgRatio)}</td>
                                                <td className={td}>{formatValue(selectedRow.caMgKRatio)}</td>
                                                <td className={td}>{formatValue(selectedRow.mgKRatio)}</td>
                                                <td className={td}>{formatValue(selectedRow.sValue)}</td>
                                                <td className={td}>{formatValue(selectedRow.naKRatio)}</td>
                                                <td className={td}>{formatValue(selectedRow.cec)}</td>
                                                <td className={td}>{formatValue(selectedRow.density)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </QCMapPanel>
                    )}
                    <div className="flex justify-end">
                        <button
                            className="btn-sm bg-primary"
                            onClick={() => props.setShow(false)}
                        >
                            {app.word('done')}
                        </button>
                    </div>
                </div>
            }
        />
    )
}

export default SampleLocationsDialog
